import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { createAuth0Client } from "@auth0/auth0-spa-js";

import { auth0Config } from "@aceso/config";
import axios from "@aceso/utils/axios";
import { getClusterAPIEndpoint } from "@aceso/utils/getClusterInfo";
import {
  createUserInHealthcare,
  fetchAuth0Me,
  handleAssignedResident,
} from "@aceso/utils/helperMethods";

const INITIALIZE = "INITIALIZE";
const SIGN_OUT = "SIGN_OUT";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  healthcareUser: null,
  roleName: null,
  tokens: null,
  error: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        ...state,
        ...action.payload,
        isInitialized: true,
      };
    case SIGN_OUT:
      return {
        ...initialState,
        isInitialized: true,
      };
    default:
      return state;
  }
};

const AuthContext = createContext(null);

const getOrgId = () => {
  if (typeof window === "undefined") {
    return process.env.NEXT_PUBLIC_AUTH0_ORG_ID || "";
  }

  return (
    window.localStorage.getItem("auth0_orgID") ||
    process.env.NEXT_PUBLIC_AUTH0_ORG_ID ||
    ""
  );
};

const getRedirectUri = () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return `${window.location.origin}/auth/sign-in`;
};

const roleIdToName = (roleId) => {
  if (!roleId) {
    return null;
  }

  if (roleId === process.env.NEXT_PUBLIC_AUTH0_ROLE_ROOT_ID) {
    return "root";
  }

  if (roleId === process.env.NEXT_PUBLIC_AUTH0_ROLE_ADMIN_ID) {
    return "admin";
  }

  if (roleId === process.env.NEXT_PUBLIC_AUTH0_ROLE_NURSE_ID) {
    return "nurse";
  }

  if (roleId === process.env.NEXT_PUBLIC_AUTH0_ROLE_RELATIVE_ID) {
    return "relative";
  }

  return null;
};

const getRoleInfo = (roles = []) => {
  const regex = /healthcare-([\w-]+)/i;

  for (const role of roles) {
    const match = role?.name?.match(regex);
    if (match) {
      return {
        roleName: match[1],
        roleId: role?.id || null,
      };
    }

    const mappedRoleName = roleIdToName(role?.id);
    if (mappedRoleName) {
      return {
        roleName: mappedRoleName,
        roleId: role?.id || null,
      };
    }
  }

  return { roleName: null, roleId: null };
};

const fetchHealthcareUser = async (userSub, accessToken) => {
  try {
    const searchParam = encodeURIComponent(userSub);
    const { data } = await axios.request({
      method: "GET",
      url: `${getClusterAPIEndpoint()}/users?search=${searchParam}`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (Array.isArray(data)) {
      return data[0] || null;
    }

    if (Array.isArray(data?.items)) {
      return data.items[0] || null;
    }

    return data?.user || null;
  } catch (error) {
    console.error("Unable to fetch healthcare user", error);
    return null;
  }
};

const buildAuthState = async (client, persistPartialState) => {
  const accessToken = await client.getTokenSilently({
    authorizationParams: {
      audience: process.env.NEXT_PUBLIC_AUTH0_AUDIENCE,
      organization: getOrgId(),
    },
  });

  const user = await client.getUser();
  const idTokenClaims = await client.getIdTokenClaims();

  const baseState = {
    isAuthenticated: true,
    user,
    healthcareUser: null,
    roleName: null,
    tokens: {
      accessToken,
      idToken: idTokenClaims?.__raw || null,
    },
  };

  persistPartialState?.(baseState);

  const orgId = getOrgId();

  const { user: detailedUser, roles } = await fetchAuth0Me(accessToken);
  const { roleName, roleId } = getRoleInfo(roles);

  if (!roleName) {
    throw new Error(
      "Your account is missing the healthcare role. Please contact an administrator."
    );
  }

  let healthcareUser = await fetchHealthcareUser(user.sub, accessToken);

  if (!healthcareUser) {
    healthcareUser = await createUserInHealthcare(
      detailedUser,
      roleId,
      orgId,
      accessToken
    );
  }

    await handleAssignedResident(detailedUser, healthcareUser);

  const fullState = {
    ...baseState,
    user: {
      ...user,
      picture: user.picture || detailedUser?.picture,
      name: user.name || detailedUser?.name,
    },
    healthcareUser,
    roleName,
  };

  persistPartialState?.(fullState);
  return fullState;
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const auth0ClientRef = useRef(null);

  const persistState = useCallback((authState) => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem("authState", JSON.stringify(authState));
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      if (typeof window === "undefined") {
        dispatch({
          type: INITIALIZE,
          payload: { ...initialState },
        });
        return;
      }

      try {
        const client = await createAuth0Client({
          domain: auth0Config.domain,
          clientId: auth0Config.clientId,
          authorizationParams: {
            audience: process.env.NEXT_PUBLIC_AUTH0_AUDIENCE,
            organization: getOrgId(),
            redirect_uri: getRedirectUri(),
          },
          cacheLocation: "localstorage",
          useRefreshTokens: true,
        });

        if (!isMounted) {
          return;
        }

        auth0ClientRef.current = client;

        if (
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          const { appState } = await client.handleRedirectCallback();
          const targetUrl = appState?.returnTo || window.location.pathname;
          window.history.replaceState({}, document.title, targetUrl);
        }

        const isAuthenticated = await client.isAuthenticated();

        if (!isAuthenticated) {
          const payload = {
            ...initialState,
            isAuthenticated: false,
            isInitialized: true,
          };
          persistState(payload);
          dispatch({ type: INITIALIZE, payload });
          return;
        }

        const authState = await buildAuthState(client, persistState);
        const payload = {
          ...authState,
          isInitialized: true,
        };

        persistState(payload);
        dispatch({ type: INITIALIZE, payload });
      } catch (error) {
        console.error("Failed to initialize Auth0", error);
        const payload = {
          ...initialState,
          error: error.message,
          isInitialized: true,
        };
        persistState(payload);
        dispatch({ type: INITIALIZE, payload });
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [persistState]);

  const signIn = useCallback(
    async (options = {}) => {
      if (typeof window === "undefined") {
        return;
      }

      if (!auth0ClientRef.current) {
        throw new Error("Auth0 is still initializing. Please try again.");
      }

      await auth0ClientRef.current.loginWithRedirect({
        authorizationParams: {
          audience: process.env.NEXT_PUBLIC_AUTH0_AUDIENCE,
          organization: getOrgId(),
          redirect_uri: getRedirectUri(),
        },
        appState: {
          returnTo: options?.returnTo || window.location.pathname,
        },
      });
    },
    []
  );

  const signOut = useCallback(async () => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.removeItem("authState");
    dispatch({ type: SIGN_OUT });

    if (auth0ClientRef.current) {
      await auth0ClientRef.current.logout({
        logoutParams: {
          returnTo: getRedirectUri(),
        },
      });
    }
  }, []);

  const resetPassword = useCallback(async () => {
    if (typeof window === "undefined") {
      return;
    }

    if (!auth0ClientRef.current) {
      throw new Error("Auth0 is still initializing. Please try again.");
    }

    await auth0ClientRef.current.loginWithRedirect({
      authorizationParams: {
        screen_hint: "reset_password",
        organization: getOrgId(),
        redirect_uri: getRedirectUri(),
      },
    });
  }, []);

  const getAccessToken = useCallback(async () => {
    if (!auth0ClientRef.current) {
      return null;
    }

    try {
      const accessToken = await auth0ClientRef.current.getTokenSilently({
        authorizationParams: {
          audience: process.env.NEXT_PUBLIC_AUTH0_AUDIENCE,
          organization: getOrgId(),
        },
      });

      const updatedState = {
        ...state,
        tokens: {
          ...(state.tokens || {}),
          accessToken,
        },
      };

      persistState(updatedState);
      return accessToken;
    } catch (error) {
      console.error("Unable to refresh Auth0 token", error);
      return null;
    }
  }, [persistState, state]);

  const signUp = useCallback(async () => {
    throw new Error(
      "Self-service sign up is disabled. Ask your administrator for an Auth0 invitation."
    );
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    window.__getAuth0Token = getAccessToken;

    return () => {
      if (window.__getAuth0Token === getAccessToken) {
        delete window.__getAuth0Token;
      }
    };
  }, [getAccessToken]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "auth0",
        signIn,
        signOut,
        resetPassword,
        signUp,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
